/**
 * @file ThemePage.js
 * @description This file contains the implementation of the ThemePage component, which allows users to customize the theme of the application.
 */

import React, { useEffect, useState } from 'react';
import ColorBar from './ColorBar';
import FileUploader from './FileUploader';
import LoginForm from './../auth/LoginForm';
import { useAuthContext } from '../../store/AuthContext';
import style from './ThemePage.module.scss';
import Header from '../header/Header';
import Navbar from '../navbar/Navbar';
import Tooltip from '../ui/tooltips/Tooltip';
import Toast from '../ui/toasts/Toast';
import { lightenColor } from '../../utils/utils';
import { isValidMobileIcon } from '../../utils/validate';
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { updateTheme, createTheme } from '../../services/themeService';
import { SecurityAttributes } from '../../utils/data/enums';
import { getText } from '../../i18n';

const ThemePage = () => {
    const {
        companyConfig: companyData,
        publicBrand,
        reloadCompanyTheme,
        companyCode,
        hasAttribute
    } = useAuthContext();
    const [primaryLogo, setPrimaryLogo] = useState(null);
    const [secondaryLogo, setSecondaryLogo] = useState(null);
    const [favicon, setFavicon] = useState(null);
    const [mobileIcon, setMobileIcon] = useState(null);
    const [initialMobileIcon, setInitialMobileIcon] = useState(null);
    const [themeName, setThemeName] = useState(null);
    const [companyConfig, setCompanyConfig] = useState(companyData);
    const [colors, setColors] = useState({
        header: { left: '#1366ff', right: '#17bdbf', type: 'gradient' },
        accessoriesBg: { left: '#1366ff', right: '#1366ff', type: 'single' },
        accessoriesFg: { left: '#FFFFFF', right: '#FFFFFF', type: 'single' },
    });

    const [appTheme, setAppTheme] = useState({});

    const updateColors = section => newColors => {
        setColors(prev => ({ ...prev, [section]: newColors }));
    };

    const [loginBackgroundOption, setLoginBackgroundOption] = useState('color'); // "color" or "image"
    const [loginBackgroundColor, setLoginBackgroundColor] = useState({
        left: '#ffffff',
        right: '#ffffff',
        type: 'single',
    });
    const [loginBackgroundImage, setLoginBackgroundImage] = useState(null);

    useEffect(() => {
        return () => {
            if (loginBackgroundImage) {
                loadImageUrl(loginBackgroundImage);
            }
        };
    }, [loginBackgroundImage]);

    const setInitialThemeValues = () => {
        if (publicBrand) {
            setThemeName(publicBrand.name);
            // setPrimaryLogo(publicBrand.primaryLogo || publicBrand.defaultLogo);
            // setSecondaryLogo(
            //   publicBrand.secondaryLogo ||
            //     publicBrand.primaryLogo ||
            //     publicBrand.defaultLogo
            // );
            setPrimaryLogo(publicBrand.primaryLogo);
            setSecondaryLogo(publicBrand.secondaryLogo);
            setFavicon(publicBrand.favicon);
            setMobileIcon(publicBrand.mobileIcon);
            setInitialMobileIcon(publicBrand.mobileIcon);
            setColors({
                header: {
                    left: publicBrand.pageHeaderColor1,
                    right:
                        publicBrand.pageHeaderColor2 ||
                        publicBrand.pageHeaderColor1,
                    type: publicBrand.pageHeaderColor2 ? 'gradient' : 'single',
                },
                accessoriesBg: {
                    left: publicBrand.accessoryColor,
                    right: publicBrand.accessoryColor,
                    type: 'single',
                },
                accessoriesFg: {
                    left: publicBrand.accessoryTextColor,
                    right: publicBrand.accessoryTextColor,
                    type: 'single',
                },
            });

            setLoginBackgroundColor({
                left: publicBrand.loginBackgroundColor || '#f8fafc',
                right: publicBrand.loginBackgroundColor || '#f8fafc',
                type: 'single',
            });
            setLoginBackgroundImage(publicBrand.loginBackgroundImage);

            if (publicBrand?.loginBackgroundImage) {
                setLoginBackgroundOption('image');
            }
        }
    };

    const discardThemeChanges = () => {
        Toast.success(getText('msg_93fda44da931'));
        setInitialThemeValues();
    };

    useEffect(() => {
        setInitialThemeValues();
    }, [publicBrand]);

    useEffect(() => {
        setThemeName(document.title);
    }, []);

    const loginFormBackgroundStyle =
        loginBackgroundOption === 'image' && loginBackgroundImage
            ? {
                backgroundImage: `url(${loadImageUrl(loginBackgroundImage)})`,
                backgroundSize: 'cover',
            }
            : {
                background:
                    loginBackgroundOption === 'color'
                        ? loginBackgroundColor.type === 'gradient'
                            ? `linear-gradient(to right, ${loginBackgroundColor.left}, ${loginBackgroundColor.right})`
                            : loginBackgroundColor.left
                        : 'none',
            };

    const updateLoginBackgroundColors = newColors => {
        setLoginBackgroundColor(newColors);
    };

    const handleBackgroundOptionChange = event => {
        setLoginBackgroundOption(event.target.value);
    };

    function loadImageUrl(imageUrl) {
        try {
            return URL.createObjectURL(imageUrl);
        } catch (e) {
            return imageUrl;
        }
    }

    const updatePrimaryLogo = file => {
        setPrimaryLogo(file);
        setCompanyConfig(prev => ({
            ...prev,
            public_brand: {
                ...(prev?.public_brand || {}),
                logo_url: file ? loadImageUrl(file) : '',
            },
        }));
    };

    useEffect(() => {
        const updatedTheme = {
            id: publicBrand?.id || 0,
            primaryLogo: primaryLogo ? loadImageUrl(primaryLogo) : null,
            secondaryLogo: secondaryLogo ? loadImageUrl(secondaryLogo) : null,
            favicon: favicon ? loadImageUrl(favicon) : null,
            mobileIcon: mobileIcon ? loadImageUrl(mobileIcon) : null,
            name: themeName,
            pageHeaderColor1: colors.header.left,
            pageHeaderColor2:
                colors.header.type === 'gradient' ? colors.header.right : null,
            accessoryColor: colors.accessoriesBg.left,
            accessoryTextColor: colors.accessoriesFg.left,
            loginBackgroundColor: loginBackgroundColor.left,
            loginBackgroundImage:
                loginBackgroundOption === 'image'
                    ? loginBackgroundImage
                        ? loadImageUrl(loginBackgroundImage)
                        : null
                    : null,
        };

        setAppTheme(updatedTheme);

        return () => {
            if (primaryLogo) {
                loadImageUrl(primaryLogo);
            }
            if (secondaryLogo) {
                loadImageUrl(secondaryLogo);
            }
            if (favicon) {
                loadImageUrl(favicon);
            }
            if (mobileIcon) {
                loadImageUrl(mobileIcon);
            }
            if (loginBackgroundImage) {
                loadImageUrl(loginBackgroundImage);
            }
        };
    }, [
        primaryLogo,
        secondaryLogo,
        favicon,
        mobileIcon,
        themeName,
        colors,
        loginBackgroundOption,
        loginBackgroundColor,
        loginBackgroundImage,
    ]);

    const handleSubmit = () => {
        const themePromise = appTheme.id
            ? updateTheme(appTheme)
            : createTheme(appTheme);

        themePromise
            .then(response => {
                Toast.success(getText('msg_d0fdcc99edf7'));
                reloadCompanyTheme(companyCode);
            })
            .catch(error => {
                Toast.error(getText('msg_6172a160eb34'));
                console.error('Unable to update Theme', error);
            });
    };

    const handleThemeNameChange = event => {
        setThemeName(event.target.value);
    };

    const background = () => css`
        --bs-table-bg-type: ${lightenColor(
        colors?.accessoriesBg.left
            ? `${colors?.accessoriesBg.left}`
            : 'inheirt',
        0.85,
    )} !important;
    `;

    const setPreviewStyles = () => {
        const { accessoriesBg, accessoriesFg } = colors;

        document.documentElement.style.setProperty(
            '--preview-border',
            accessoriesBg.type === 'single'
                ? `1px solid ${accessoriesBg.left}`
                : 'none',
        );

        document.documentElement.style.setProperty(
            '--preview-background',
            accessoriesBg.type === 'single'
                ? accessoriesBg.left
                : `linear-gradient(to right, ${accessoriesBg.left}, ${accessoriesBg.right})`,
        );

        document.documentElement.style.setProperty(
            '--preview-color',
            accessoriesFg.left || 'inherit',
        );
    };

    useEffect(() => {
        setPreviewStyles();
    }, [colors]);

    const renderTable = () => {
        return (
            <div className="table-responsive-xxl w-100">
                <table className="table table-hover align-middle ing-table-striped">
                    <thead className="custom-table-header table-light">
                        <tr>
                            <th className="text-center PopulationGroup_checkboxColumnHead__noHC-">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                />
                            </th>
                            <th>
                                {getText('msg_709a23220f2c')}{' '}
                                <span className="px-2 PopulationGroup_sortArrowIcon__1-7rs">
                                    <svg
                                        width="11"
                                        height="6"
                                        viewBox="0 0 11 6"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M1 5.25L5.25 0.75L9.5 5.25"
                                            stroke="#2F2B43"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                    </svg>
                                </span>
                            </th>
                            <th>{getText('msg_a85ae5652dea')} </th>
                            <th>{getText('msg_5d56281f8457')} </th>
                            <th>{getText('msg_f70b90dda9b6')} </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            className={style.tableHoverAction}
                            css={background()}
                        >
                            <td className="text-center">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                />
                            </td>
                            <td
                                className={style.itemPageName}
                                style={{
                                    color: `${colors?.accessoriesBg.left
                                            ? `${colors?.accessoriesBg.left}`
                                            : 'inheirt'
                                        }`,
                                }}
                            >
                                {getText('msg_7ddd97722b4a')}
                            </td>
                            <td>-</td>
                            <td>-</td>
                            <td>-</td>
                        </tr>
                        <tr>
                            <td className="text-center">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                />
                            </td>
                            <td className="item-page-name">{getText('msg_2a33b33d0d7c')}</td>
                            <td>-</td>
                            <td>-</td>
                            <td>-</td>
                        </tr>
                        <tr>
                            <td className="text-center">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                />
                            </td>
                            <td className="item-page-name">{getText('msg_902698b5a14c')}</td>
                            <td>-</td>
                            <td>-</td>
                            <td>-</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className={`page-content ${style.container}`}>
            <div className="card">
                <div className="card-body px-0 py-4 b">
                    <div className="d-flex flex-column align-items-start pb-3">
                        <p className="page-title float-start card-title ms-4 mb-0">
                            {getText('msg_a797e30923ac')}
                        </p>
                        <p
                            className="ing-portals-subtitle mt-1"
                            style={{ marginTop: '10px' }}
                        >
                            {getText('msg_5330de75a59f')}
                            <br />
                            {getText('msg_77692069d96b')}
                        </p>{' '}
                    </div>

                    <div className="col-lg-8 px-3 mt-5 m-auto">
                        <section>
                            <div
                                className={`form-group col-12 col-sm-12 col-md-12 py-4`}
                            >
                                <label htmlFor="themeName">{getText('msg_4fb43108e78f')}</label>
                                <input
                                    id="themeName"
                                    type="text"
                                    maxLength="50"
                                    className={`form-control`}
                                    value={themeName}
                                    onChange={handleThemeNameChange}
                                    name="themeName"
                                    placeholder={getText('msg_e9c59a05e43f')}
                                />
                                <small className="form-text text-muted">
                                    {getText('msg_971d6ded4edf')} {themeName?.length}{getText('msg_2fd301945bd1')}
                                </small>
                            </div>

                            <div className="my-5">
                                <label className="pb-3">
                                    {getText('msg_f76e9d2cf834')}
                                    <sup>
                                        <Tooltip
                                            html="Login Screen Logo will display on the login screen. If this is the only logo uploaded, it will also appear everywhere a logo is shown throughout the app."
                                            placement="right"
                                        >
                                            <i
                                                className="bi bi-info-circle"
                                                style={{
                                                    paddingLeft: '5px',
                                                    fontSize: '13px',
                                                    color: '#1366ff',
                                                }}
                                            ></i>
                                        </Tooltip>
                                    </sup>
                                </label>
                                <FileUploader
                                    id="primary-image-upload"
                                    onFileChange={file =>
                                        updatePrimaryLogo(file)
                                    }
                                    initialFile={primaryLogo}
                                    accept=".png,.jpg,.jpeg"
                                    inputText={getText('msg_113df7a7b872')}
                                    locked={hasAttribute(
                                        SecurityAttributes.ThemeReadOnly
                                    )}
                                />
                            </div>

                            <div className="my-5">
                                <label className="pb-3">
                                    {getText('msg_afb30588070a')}
                                    <sup>
                                        <Tooltip html="Navigation Menu Logo will display above the navigation menu and everywhere a logo is shown throughout the site.">
                                            <i
                                                className="bi bi-info-circle"
                                                style={{
                                                    paddingLeft: '5px',
                                                    fontSize: '13px',
                                                    color: '#1366ff',
                                                }}
                                            ></i>
                                        </Tooltip>
                                    </sup>
                                </label>
                                <FileUploader
                                    id="secondary-image-upload"
                                    onFileChange={file =>
                                        setSecondaryLogo(file)
                                    }
                                    initialFile={secondaryLogo}
                                    accept=".png,.jpg,.jpeg"
                                    inputText={getText('msg_113df7a7b872')}
                                    locked={hasAttribute(
                                        SecurityAttributes.ThemeReadOnly
                                    )}
                                />
                            </div>

                            <div className="my-5">
                                <label className="pb-3">
                                    {getText('msg_b0f47aaf773c')}
                                    <sup>
                                        <Tooltip html="The favicon is the small icon that appears in browser tabs and bookmarks.">
                                            <i
                                                className="bi bi-info-circle"
                                                style={{
                                                    paddingLeft: '5px',
                                                    fontSize: '13px',
                                                    color: '#1366ff',
                                                }}
                                            ></i>
                                        </Tooltip>
                                    </sup>
                                </label>
                                <FileUploader
                                    id="favicon-upload"
                                    onFileChange={file => setFavicon(file)}
                                    initialFile={favicon}
                                    accept=".ico,.png"
                                    inputText={getText('msg_230521671806')}
                                    locked={hasAttribute(
                                        SecurityAttributes.ThemeReadOnly
                                    )}
                                />
                            </div>

                            <div className="my-5">
                                <label className="pb-3">
                                    {getText('msg_6b23c2d851de')}
                                    <sup>
                                        <Tooltip html="The mobile icon is used for mobile app installations and home screen shortcuts. Requirements: PNG format only, square dimensions (width = height), minimum 512x512 pixels.">
                                            <i
                                                className="bi bi-info-circle"
                                                style={{
                                                    paddingLeft: '5px',
                                                    fontSize: '13px',
                                                    color: '#1366ff',
                                                }}
                                            ></i>
                                        </Tooltip>
                                    </sup>
                                </label>
                                <FileUploader
                                    id="mobile-icon-upload"
                                    onFileChange={file => setMobileIcon(file)}
                                    initialFile={mobileIcon}
                                    accept=".png"
                                    inputText={
                                        <>
                                            {getText('msg_32e62dcbf83a')}
                                            <br />
                                            {getText('msg_f9bab8695ec5')}
                                        </>
                                    }
                                    customValidation={isValidMobileIcon}
                                    locked={hasAttribute(
                                        SecurityAttributes.ThemeReadOnly
                                    )}
                                />
                            </div>
                        </section>

                        <section className="py-5">
                            <label className="mb-3">
                                {getText('msg_c9b9c46b1c2c')}
                            </label>
                            <div className="d-flex inline-flex w-100 gap-3 pb-3">
                                <input
                                    type="radio"
                                    id="background-color"
                                    name="backgroundOption"
                                    value="color"
                                    checked={loginBackgroundOption === 'color'}
                                    onChange={handleBackgroundOptionChange}
                                />
                                <ColorBar
                                    initialColors={loginBackgroundColor}
                                    onChangeComplete={
                                        updateLoginBackgroundColors
                                    }
                                    allowGradient={false}
                                    readOnly={hasAttribute(
                                        SecurityAttributes.ThemeReadOnly
                                    )}
                                />
                            </div>

                            <div className="d-flex inline-flex w-100 gap-3 pb-3">
                                <input
                                    type="radio"
                                    id="background-image"
                                    name="backgroundOption"
                                    value="image"
                                    checked={loginBackgroundOption === 'image'}
                                    onChange={handleBackgroundOptionChange}
                                    className={style.backgroundImage}
                                />

                                <FileUploader
                                    id="login-background-image-upload"
                                    onFileChange={setLoginBackgroundImage}
                                    initialFile={loginBackgroundImage}
                                    accept=".png,.jpg,.jpeg"
                                    inputText={getText('msg_70c69e310de3')}
                                    locked={hasAttribute(
                                        SecurityAttributes.ThemeReadOnly
                                    )}
                                />
                            </div>
                        </section>

                        <div className="py-3">
                            <ColorBar
                                label={getText('msg_31341c6f0c7a')}
                                initialColors={colors.header}
                                onChangeComplete={updateColors('header')}
                                allowGradient={true}
                                toolTipText=""
                                readOnly={hasAttribute(
                                    SecurityAttributes.ThemeReadOnly
                                )}
                            />
                        </div>

                        <div className="py-3">
                            <ColorBar
                                label={getText('msg_700530084ce1')}
                                initialColors={colors.accessoriesBg}
                                onChangeComplete={updateColors('accessoriesBg')}
                                allowGradient={false}
                                toolTipText={getText('msg_fbe57bdc391c')}
                                readOnly={hasAttribute(
                                    SecurityAttributes.ThemeReadOnly
                                )}
                            />
                        </div>
                        <div className="py-3">
                            <ColorBar
                                label={getText('msg_4647f7d57eb6')}
                                initialColors={colors.accessoriesFg}
                                onChangeComplete={updateColors('accessoriesFg')}
                                allowGradient={false}
                                toolTipText={getText('msg_ef55de693934')}
                                readOnly={hasAttribute(
                                    SecurityAttributes.ThemeReadOnly
                                )}
                            />
                        </div>
                    </div>
                    <div className="d-flex mx-4 mt-5">
                        <h5 className="pr-2 me-3">{getText('msg_f1fbb2b43dca')}</h5>
                        <hr className={`w-100 ${style.previewDivider}`}></hr>
                    </div>
                    <div className={`col-lg-8 px-3 m-auto`}>
                        <div
                            className={`autoMargin px-5  ${style.logingPreview}`}
                            style={loginFormBackgroundStyle}
                        >
                            <div
                                className={`ing-card modal-content ${style.authContainer}`}
                            >
                                <LoginForm
                                    onSuccess={() => { }}
                                    handleAuthError={() => { }}
                                    authData={{ companyConfig }}
                                    demoOnly={true}
                                    styleOverride={appTheme}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="d-block">
                        <div className={style.appPreviewContainer}>
                            <Header styleOverride={appTheme} />
                            <div className="middle">
                                <div className="d-flex">
                                    <Navbar styleOverride={appTheme} />
                                    <div className={`${style.appPreview}`}>
                                        <div className={`page-content h-100`}>
                                            <div className="card h-100">
                                                <div className="card-body px-0 py-4 b">
                                                    <div className="d-flex justify-content-between align-items-center pb-3 border-bottom">
                                                        <p className="page-title float-start card-title ms-4 mb-0">
                                                            {getText('msg_f1fbb2b43dca')}
                                                        </p>

                                                        <div className="float-end d-flex align-items-center">
                                                            <span
                                                                type="button"
                                                                onClick={() => { }}
                                                                className="ing-text-buttons mx-3"
                                                            >
                                                                <i className="bi bi-trash3 me-2" />
                                                                {getText('msg_f6fdbe48dc54')}
                                                            </span>
                                                            <span
                                                                type="button"
                                                                onClick={() => { }}
                                                                className="ing-text-buttons mx-3"
                                                            >
                                                                <i className="bi bi-copy me-2" />
                                                                {getText('msg_972d57379db3')}
                                                            </span>
                                                            <div to="new-portal-page">
                                                                <span
                                                                    onClick={() => { }}
                                                                    type="button"
                                                                    className={`ms-3 me-4  button-base ing-button-primary-left ${style.ingButtonPrimaryLeft}`}
                                                                >
                                                                    <i className="bi bi-plus-lg me-1" />
                                                                    {getText('msg_61cc55aa0453')}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* {renderTable()} */}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-center mb-5 me-4">
                    <button
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title={getText('msg_a5403eda9f70')}
                        type="button"
                        onClick={() => discardThemeChanges()}
                        className="ing-popup-blue-negative submit-button-form mx-3"
                    >
                        <i className="bi bi-x me-2" />
                        {getText('msg_77dfd2135f4d')}
                    </button>
                    {!hasAttribute(
                        SecurityAttributes.ThemeReadOnly
                    ) && (
                            <button
                                type="button"
                                onClick={handleSubmit}
                                className="mx-3 ing-button-primary-left align-self-baseline"
                            >
                                <i className="bi bi-check-lg me-2" />
                                {getText('msg_efc007a393f6')}
                            </button>
                        )}
                </div>
            </div>
        </div>
    );
};

export default ThemePage;
